// Type-aware resolvers for a tracked subject, mirrored from the mobile app's
// app_design.dart. A subject's "kind" (vehicle / person / pet / item) drives
// which marker icon shows and which detail rows/actions the popup renders.
// Reads the DeviceLiveViewResource fields the personal live view returns.

import {statusColors} from "./appDesign.js";
import {personaByAssetTypeId} from "./personaService.js";

// vehicle | person | pet | item
export function subjectKindOf(device) {
    const persona = personaByAssetTypeId(device?.asset_type_id);
    if (persona) {
        // Table-driven subject_kind when available (catalog), else map the
        // hardcoded persona id.
        if (persona.kind) return persona.kind;
        if (persona.id === "vehicle" || persona.id === "bike") return "vehicle";
        if (persona.id === "kid" || persona.id === "senior") return "person";
        if (persona.id === "pet") return "pet";
        return "item"; // luggage, equipment, other
    }
    const category = (device?.asset_type_category || "").toLowerCase();
    if (category === "vehicle") return "vehicle";
    const name = (device?.asset_type_name || "").toLowerCase();
    if (name.includes("pet") || name.includes("dog") || name.includes("cat")) return "pet";
    if (name.includes("kid") || name.includes("child") || name.includes("senior") || name.includes("person")) {
        return "person";
    }
    return "item";
}

const KIND_FALLBACK_ICON = {
    vehicle: "mdi-car",
    person: "mdi-account",
    pet: "mdi-paw",
    item: "mdi-package-variant",
};

// Persona icon when known, else a generic icon for the kind.
export function subjectIcon(device) {
    const persona = personaByAssetTypeId(device?.asset_type_id);
    if (persona) return persona.icon;
    return KIND_FALLBACK_ICON[subjectKindOf(device)];
}

// Freshness/status resolvers live in subjectStatus.js (pure, node-testable);
// re-exported here so existing call sites keep one import.
import {isOnline, isReporting, lastSeenMinutes, lastSeenAgo, freshness, freshnessLabel, subjectStatusOf} from "./subjectStatus.js";
export {isOnline, isReporting, lastSeenMinutes, lastSeenAgo, freshness, freshnessLabel, subjectStatusOf};


const STATUS_META = {
    moving: {color: statusColors.moving, label: "Moving", pulses: true},
    idling: {color: statusColors.warn, label: "Idling", pulses: false},
    live: {color: statusColors.live, label: "Live", pulses: false},
    lowBattery: {color: statusColors.warn, label: "Low battery", pulses: false},
    stale: {color: statusColors.warn, label: "Stale", pulses: false},
    offline: {color: statusColors.offline, label: "Offline", pulses: false},
    // Deliberate pause (asset disabled) vs an enabled phone with permission off.
    sharingOff: {color: statusColors.offline, label: "Sharing paused", pulses: false},
    locationOff: {color: statusColors.warn, label: "Location off", pulses: false},
    // Never heard from - the strongest form of non-reporting, so it borrows the
    // offline grey for the freshness dot.
    neverReported: {color: statusColors.offline, label: "Not yet reporting", pulses: false},
};

export function statusMeta(status) {
    return STATUS_META[status] ?? STATUS_META.offline;
}

// Accessible filled-badge palette for the status chip (device list) and status
// pill (map popup), keyed by subjectStatusOf so both surfaces agree. Fills are
// dark enough that white text meets WCAG AA (>= 4.5:1). Distinct from
// statusColors, which is used for marker fills and freshness dots (filled
// shapes, no text-contrast requirement).
const STATUS_BADGE_COLOR = {
    moving: "#1976D2",   // 4.6:1 white
    idling: "#D84315",   // 4.8:1 white
    live: "#2E7D32",     // 5.1:1 white
    stale: "#B45309",    // 4.7:1 white - amber
    offline: "#8B0000",  // ~13:1 white - non-reporting, high-visibility dark red
    sharingOff: "#616161", // 5.6:1 white - grey, deliberate pause
    locationOff: "#B45309", // 4.7:1 white - amber, enabled but OS permission off
    neverReported: "#8B0000", // ~13:1 white - shares the non-reporting dark red
};
// Labels/icons are kind-aware: "Parked" + car icons only make sense for
// vehicles. People/pets/items get neutral wording and non-vehicle icons.
const STATUS_BADGE_VEHICLE = {
    moving: {label: "Moving", icon: "mdi-navigation"},
    idling: {label: "Idling", icon: "mdi-car-clock"},
    live: {label: "Parked", icon: "mdi-car-brake-parking"},
    stale: {label: "Stale", icon: "mdi-clock-alert-outline"},
    offline: {label: "Offline", icon: "mdi-car-off"},
    sharingOff: {label: "Sharing paused", icon: "mdi-map-marker-off"},
    locationOff: {label: "Location off", icon: "mdi-map-marker-alert"},
    neverReported: {label: "Not yet reporting", icon: "mdi-map-marker-question-outline"},
};
const STATUS_BADGE_GENERIC = {
    moving: {label: "Moving", icon: "mdi-navigation"},
    idling: {label: "Idle", icon: "mdi-timer-sand"},
    live: {label: "Stationary", icon: "mdi-map-marker"},
    stale: {label: "Stale", icon: "mdi-clock-alert-outline"},
    offline: {label: "Offline", icon: "mdi-signal-off"},
    sharingOff: {label: "Sharing paused", icon: "mdi-map-marker-off"},
    locationOff: {label: "Location off", icon: "mdi-map-marker-alert"},
    neverReported: {label: "Not yet reporting", icon: "mdi-map-marker-question-outline"},
};

export function statusBadge(device) {
    const s = subjectStatusOf(device);
    const set = subjectKindOf(device) === "vehicle" ? STATUS_BADGE_VEHICLE : STATUS_BADGE_GENERIC;
    const meta = set[s] ?? set.offline;
    return {label: meta.label, icon: meta.icon, color: STATUS_BADGE_COLOR[s] ?? STATUS_BADGE_COLOR.offline};
}

// Spelled-out duration since a timestamp: "2 mins", "1 hr 5 mins", "3 days".
// (Shared by the map popup and the device list so both read the same way.)
export function durationWords(raw) {
    if (!raw) return "";
    let s = String(raw).trim();
    if (!s) return "";
    s = s.replace(" ", "T");
    if (!s.endsWith("Z") && !/[+-]\d\d:?\d\d$/.test(s)) s += "Z";
    const diffMs = Date.now() - new Date(s).getTime();
    if (isNaN(diffMs) || diffMs < 0) return "";
    const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
    const mins = Math.max(1, Math.floor(diffMs / 60000));
    if (mins < 60) return plural(mins, "min");
    const hours = Math.floor(mins / 60);
    const rm = mins % 60;
    if (hours < 24) return rm > 0 ? `${plural(hours, "hr")} ${plural(rm, "min")}` : plural(hours, "hr");
    const days = Math.floor(hours / 24);
    const rh = hours % 24;
    return rh > 0 ? `${plural(days, "day")} ${plural(rh, "hr")}` : plural(days, "day");
}

// Life360-style presence: "Moving for X" / "Idling for X" / "Here for X".
// Offline returns "" (the "last heard"/"updated" line already owns that time).
export function presenceText(device) {
    const st = subjectStatusOf(device);
    if (st === "moving") {
        const d = durationWords(device?.motion_started_at);
        return d ? `Moving for ${d}` : "";
    }
    if (st === "idling") {
        const d = durationWords(device?.motion_started_at);
        return d ? `Idling for ${d}` : "";
    }
    if (st === "live") {
        const d = durationWords(device?.motion_ended_at);
        return d ? `Here for ${d}` : "";
    }
    return "";
}
